import React, { useState } from 'react';
import axios from 'axios';
import TransactionHistory from '../components/TransactionHistory';
import BankrollStorage from '../components/BankrollStorage';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/formatting';

function FinancePage() {
    const [verifying, setVerifying] = useState(false);
    const [verificationResult, setVerificationResult] = useState(null);  // Fixed missing parenthesis

    const verifyBankroll = async () => {
        setVerifying(true);
        try {
            const response = await axios.post('/api/bankroll/recalculate/');  // This matches the DRF action URL pattern
            setVerificationResult(response.data);
        } catch (err) {
            setVerificationResult({
                error: err.response?.data?.error || 'Failed to verify bankroll'
            });
        } finally {
            setVerifying(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
            <div className="space-y-4 sm:space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Finance</h1>
                    <button
                        onClick={verifyBankroll}
                        disabled={verifying}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-primary-600 text-white rounded-lg
                                 hover:bg-primary-700 focus:outline-none focus:ring-2
                                 focus:ring-primary-500 focus:ring-offset-2
                                 disabled:opacity-50 transition-colors text-sm"
                    >
                        <ArrowPathIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${verifying ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">Verify Balance</span>
                    </button>
                </div>

                {verificationResult && (
                    <div className={`p-4 rounded-lg ${
                        verificationResult.error
                            ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : verificationResult.has_issues
                            ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                            : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    }`}>
                        <div className="font-semibold">
                            {verificationResult.error || verificationResult.message}
                        </div>
                        {verificationResult.difference && (
                            <div className="mt-2 text-sm">
                                Balance adjusted by: {formatCurrency(verificationResult.difference)}
                            </div>
                        )}
                        {verificationResult.issues && verificationResult.issues.length > 0 && (
                            <div className="mt-3 space-y-2">
                                {verificationResult.issues.map((issue, index) => (
                                    <div key={index} className="text-sm">
                                        {issue}
                                    </div>
                                ))}
                            </div>
                        )}
                        {verificationResult.storage_sum && verificationResult.new_balance && (
                            <div className="mt-3 text-sm space-y-1">
                                <div>Bankroll: {formatCurrency(parseFloat(verificationResult.new_balance))}</div>
                                <div>Storage Total: {formatCurrency(parseFloat(verificationResult.storage_sum))}</div>
                            </div>
                        )}
                    </div>
                )}
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* Bankroll Storage - appears first on mobile, second on desktop */}
                    <div className="order-1 lg:order-2">
                        <BankrollStorage />
                    </div>
                    {/* Transaction History - appears second on mobile, first on desktop */}
                    <div className="order-2 lg:order-1 bg-[#faf8f5] dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-3 sm:p-6">
                        <TransactionHistory />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FinancePage;